import AddIcon from '@mui/icons-material/Add'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import EditIcon from '@mui/icons-material/Edit'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { CASE_CLIENT_ROLE_LABELS, CASE_TYPE_LABELS, type CaseDetail } from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CaseClientsPanel } from '../components/cases/CaseClientsPanel'
import { CaseDialog } from '../components/cases/CaseDialog'
import { CasePaymentsPanel } from '../components/cases/CasePaymentsPanel'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { ContactsPanel } from '../components/contacts/ContactsPanel'
import { CaseDocumentsPanel } from '../components/documents/CaseDocumentsPanel'
import { ApplyTemplateDialog } from '../components/tasks/ApplyTemplateDialog'
import { TaskDialog } from '../components/tasks/TaskDialog'
import { TasksTable } from '../components/tasks/TasksTable'
import { api } from '../lib/api'
import { formatDate, formatMoney } from '../lib/format'
import { qk } from '../lib/queryClient'

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  )
}

export function CaseDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.case(id),
    queryFn: () => api.get<CaseDetail>(`/cases/${id}`),
    enabled: Boolean(id),
  })

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.case(id) })

  const attachContact = useMutation({
    mutationFn: (vars: { contactId: string; role: string }) => api.post(`/cases/${id}/contacts`, vars),
    onSuccess: invalidate,
  })

  const changeRole = useMutation({
    mutationFn: (vars: { contactId: string; role: string }) =>
      api.patch(`/cases/${id}/contacts/${vars.contactId}`, { role: vars.role }),
    onSuccess: invalidate,
  })

  const detachContact = useMutation({
    mutationFn: (contactId: string) => api.del(`/cases/${id}/contacts/${contactId}`),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: () => api.del(`/cases/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cases'] })
      navigate('/cases')
    },
  })

  if (isPending) return <LoadingBlock />
  if (error) return <ErrorAlert error={error} onRetry={() => void refetch()} />
  if (!data) return <EmptyState title="התיק לא נמצא" />

  // A case inside a project keeps only its unit; the street and city are the project's.
  const street = data.street ?? data.project?.street ?? null
  const city = data.city ?? data.project?.city ?? null
  const address = [street, data.buildingNo, data.aptNo && `דירה ${data.aptNo}`, city]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <PageHeader
        title={data.name}
        subtitle={address || undefined}
        actions={
          <>
            <IconButton onClick={() => navigate('/cases')} title="חזרה לרשימה">
              <ArrowForwardIcon />
            </IconButton>
            <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
              עריכה
            </Button>
            <Button color="error" onClick={() => setConfirmDelete(true)}>
              מחיקה
            </Button>
          </>
        }
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip label={CASE_TYPE_LABELS[data.caseType]} />
        <Chip variant="outlined" label={CASE_CLIENT_ROLE_LABELS[data.clientRole]} />
        {data.project ? (
          <Chip
            variant="outlined"
            color="primary"
            label={data.project.displayName}
            onClick={() => navigate(`/projects/${data.project?.id}`)}
          />
        ) : null}
      </Stack>

      {remove.error ? <ErrorAlert error={remove.error} /> : null}

      <Tabs value={tab} onChange={(_e, next) => setTab(next)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="פרטים" />
        <Tab label={`לקוחות (${data.clients.length})`} />
        <Tab label={`אנשי קשר (${data.contacts.length})`} />
        <Tab label="משימות" />
        <Tab label="מסמכים" />
        <Tab label="תשלומים" />
      </Tabs>

      {tab === 0 ? (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Grid container spacing={2.5}>
            <DetailRow label="שם התיק" value={data.name} />
            <DetailRow label="סוג" value={CASE_TYPE_LABELS[data.caseType]} />
            <DetailRow label="צד הלקוחות" value={CASE_CLIENT_ROLE_LABELS[data.clientRole]} />
            <DetailRow label="פרויקט" value={data.project?.displayName ?? null} />
            <DetailRow
              label="תאריך העסקה"
              value={data.transactionDate ? formatDate(data.transactionDate) : null}
            />
            <DetailRow
              label="שכר טרחה"
              value={data.feeAmount === null ? null : formatMoney(data.feeAmount)}
            />
            {data.caseType === 'PROPERTY' ? (
              <>
                <DetailRow
                  label={data.project ? 'עיר (מהפרויקט)' : 'עיר'}
                  value={city}
                />
                <DetailRow
                  label={data.project ? 'רחוב (מהפרויקט)' : 'רחוב'}
                  value={street}
                />
                <DetailRow label="מספר בניין" value={data.buildingNo} />
                <DetailRow label="מספר דירה" value={data.aptNo} />
                {data.project ? (
                  <>
                    <DetailRow label="גוש (מהפרויקט)" value={data.project.gush} />
                    <DetailRow label="חלקה (מהפרויקט)" value={data.project.parcel} />
                    <DetailRow label="חברה יזמית (מהפרויקט)" value={data.project.developerName} />
                  </>
                ) : null}
              </>
            ) : null}
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">
                תיאור
              </Typography>
              <Typography variant="body2">{data.description || '—'}</Typography>
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      {tab === 1 ? <CaseClientsPanel caseId={id} clients={data.clients} /> : null}

      {tab === 2 ? (
        <ContactsPanel
          contacts={data.contacts}
          explainInheritance
          busy={attachContact.isPending || detachContact.isPending || changeRole.isPending}
          error={attachContact.error ?? detachContact.error ?? changeRole.error}
          onAttach={(contactId, role) => attachContact.mutate({ contactId, role })}
          onRoleChange={(contactId, role) => changeRole.mutate({ contactId, role })}
          onDetach={(contactId) => detachContact.mutate(contactId)}
        />
      ) : null}

      {tab === 3 ? (
        <>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1 }}>
            <Button startIcon={<PlaylistAddIcon />} onClick={() => setTemplateOpen(true)}>
              יצירה מתבנית
            </Button>
            <Button startIcon={<AddIcon />} onClick={() => setTaskOpen(true)}>
              משימה חדשה בתיק
            </Button>
          </Stack>
          <TasksTable
            query={{ caseId: id, pageSize: 200 }}
            onOpenTask={setOpenTaskId}
            hiddenColumns={['caseName']}
            height={520}
          />
        </>
      ) : null}

      {tab === 4 ? (
        <CaseDocumentsPanel
          caseId={id}
          clients={data.clients.map((row) => ({
            clientId: row.clientId,
            clientName: row.clientName,
          }))}
        />
      ) : null}

      {tab === 5 ? <CasePaymentsPanel caseId={id} /> : null}

      {data.clients.length === 0 && tab === 4 ? (
        <Typography variant="caption" color="text.secondary">
          <Link component="button" type="button" onClick={() => setTab(1)}>
            יש להוסיף לקוחות לתיק
          </Link>{' '}
          כדי לשייך מסמכים ללקוח מסוים.
        </Typography>
      ) : null}

      <CaseDialog open={editOpen} caseRow={data} onClose={() => setEditOpen(false)} />
      <TaskDialog open={taskOpen} defaults={{ caseId: id }} onClose={() => setTaskOpen(false)} />
      <TaskDialog open={Boolean(openTaskId)} taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
      <ApplyTemplateDialog
        open={templateOpen}
        defaults={{ caseId: id }}
        onClose={() => setTemplateOpen(false)}
        onApplied={setOpenTaskId}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="מחיקת תיק"
        message="התיק, שיוכי הלקוחות ואנשי הקשר שלו והמסמכים שבו יימחקו לצמיתות. להמשיך?"
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  )
}
